import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import 'particles.js';
import fotlogin from '../../assets/fotlogin.svg';
import errorsvg from '../../assets/error.svg';
import { CenterContainer, StyledContainer, StyledFormContainer } from './styled';
import { useParticleJS } from './hook';
import { ChangePasswordRequest, Return, changePasswordReq } from './api';
import { getErrorMessage, getHashSearchStr, getTGUsername } from '../../utils/utils';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Label, PasswordInput, Spinner } from '@tigergraph/tiger-ui';
import { toast } from 'sonner';

export default function ChangePassword() {
  const [searchParams] = useSearchParams();
  useParticleJS();

  const defaultUsername = getTGUsername() || decodeURIComponent(searchParams.get('username') || '');
  const nameInputDisabled = !!defaultUsername;
  const [username, setUserName] = useState(defaultUsername);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const { isLoading, mutate: changePassword } = useMutation<Return<void>, AxiosError, ChangePasswordRequest>(
    'change-password',
    changePasswordReq
  );

  function handlFormChange(type: string) {
    return (e) => {
      switch (type) {
        case 'username':
          setUserName(e.target.value);
          break;
        case 'password':
          setCurrentPassword(e.target.value);
          break;
        case 'newPassword':
          setNewPassword(e.target.value);
          break;
        case 'confirmPassword':
          setConfirmPassword(e.target.value);
          break;
        default:
          break;
      }
    };
  }

  const handleSubmit = () => {
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match');
      return;
    }

    changePassword(
      { name: username, currentPassword, password: newPassword },
      {
        onError(err) {
          if (err.response?.status === 401) {
            setErrorMsg('Your username or current password is incorrect.');
          } else {
            setErrorMsg(getErrorMessage(err));
          }
        },
        onSuccess() {
          toast.success('Password changed successfully', {
            position: 'bottom-center',
          });

          const searchStr = getHashSearchStr();
          window.location.href = `/#/login${searchStr ? '?' + searchStr : ''}`;
        },
      }
    );
  };

  useEffect(() => {
    setErrorMsg('');
  }, [username, currentPassword, newPassword, confirmPassword]);

  return (
    <StyledContainer id="particles-js">
      <StyledFormContainer>
        <div className="pb-6 border-b border-[rgba(212,218,223,0.4)]">
          <CenterContainer>
            <img className="mx-auto" src={fotlogin} alt="" />
          </CenterContainer>
        </div>
        <div>
          <Label className="my-2">Username</Label>
          <Input disabled={nameInputDisabled} value={username} onChange={handlFormChange('username')} />
        </div>
        <div>
          <Label className="my-2">Password</Label>
          <PasswordInput value={currentPassword} onChange={handlFormChange('password')} />
        </div>
        <div>
          <Label className="my-2">New Password</Label>
          <PasswordInput value={newPassword} onChange={handlFormChange('newPassword')} />
        </div>
        <div>
          <Label className="my-2">Confirm Password</Label>
          <PasswordInput value={confirmPassword} onChange={handlFormChange('confirmPassword')} />
        </div>
        <div className="flex font-[Roboto] not-italic font-normal text-xs text-[#B93535] min-h-4 mt-[-2px] mb-[10px]">
          {!!errorMsg && (
            <>
              <img className="mr-[2px]" src={errorsvg} alt="" />
              {errorMsg}
            </>
          )}
        </div>
        <div className="flex w-full items-center justify-center">
          <CenterContainer>
            <Button
              onClick={handleSubmit}
              type="submit"
              disabled={!username || !currentPassword || !newPassword || !confirmPassword || isLoading}
            >
              {isLoading ? <Spinner size="small" className="text-[var(--background)]" /> : 'Change Password'}
            </Button>
          </CenterContainer>
        </div>
      </StyledFormContainer>
    </StyledContainer>
  );
}
